import { type FilterCategory, type GridWithFilters } from '@strata/react-grid';
import { type ComponentProps } from 'react';

import { getReferrals, type ReferralSummary } from 'api/apertureAPI';
import { handleError } from 'services/Error';
import { type AppThunk } from 'store';
import {
    setAdditionalReferralsData,
    setCurrentPage,
    setPageData,
} from 'store/referrals/referralListSlice';

export const fetchReferrals =
    ({
        segmentId,
        searchText,
        supplierNpi,
        prescriberNpi,
        activeFilters,
    }: {
        segmentId?: string;
        searchText?: string;
        supplierNpi?: string;
        prescriberNpi?: string;
        activeFilters: ComponentProps<typeof GridWithFilters>['activeFilters'];
    }): AppThunk =>
    async (dispatch, getState) => {
        try {
            const page = getState().referralList.currentPage;
            const response = await getReferrals({
                segmentId,
                page,
                searchText,
                supplierNpi,
                prescriberNpi,
                activeFilters,
            });

            // Additional data only returned w/ first page
            if (page === 0) {
                const filterCategoryTitleMap: Partial<Record<keyof ReferralSummary, string>> = {
                    therapeutic_category: 'Therapeutic Category',
                    product_service_description: 'Product',
                };
                const filterCategories: FilterCategory<ReferralSummary>[] = [
                    ...(response?.filters?.map(({ key, values }) => ({
                        key,
                        title: filterCategoryTitleMap[key] ?? '',
                        customValues: values?.map((value) => ({ value })) ?? [],
                    })) ?? []),
                    {
                        key: 'is_brand',
                        title: 'Brand/Generic',
                        customValues: [
                            { value: 'true', text: 'Brand' },
                            { value: 'false', text: 'Generic' },
                        ],
                    },
                ];
                dispatch(
                    setAdditionalReferralsData({
                        npi: supplierNpi ?? prescriberNpi,
                        referralCount: response?.count,
                        filteredCount: response?.filtered_count,
                        filterCategories,
                    }),
                );
            }

            dispatch(setPageData({ referrals: response?.referrals, page }));
        } catch (err) {
            handleError('An error occurred retrieving referrals.', err as Error);
        }
    };

export const updatePage =
    (currentPage: number): AppThunk =>
    (dispatch) => {
        dispatch(setCurrentPage({ currentPage }));
    };
